import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Grid, IconButton, Typography, CircularProgress, Snackbar, Alert } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { getPublicServiceDetail, getCurrentProfile } from '../../actions/profile';
import { getQuoteDetail } from '../../actions/quote';
import { createBooking, validarPromo } from '../../actions/bookings';
import { getDurationMinutes } from '../../components/booking/bookingSlotUtils';
import { pagosDisponibles } from '../../utils/pagos';

// Booking Components
import BookingOptions from '../../components/booking/BookingOptions';
import BookingPayment from '../../components/booking/BookingPayment';
import BookingSummary from '../../components/booking/BookingSummary';
import BookingDateTime from '../../components/booking/BookingDateTime';
import BookingMessage from '../../components/booking/BookingMessage';
import BookingAddress from '../../components/booking/BookingAddress';
import BookingInvoice from '../../components/booking/BookingInvoice';
import BookingConfirmation from '../../components/booking/BookingConfirmation';

const BookingContainer = () => {
  const { userId, serviceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get('quoteId');

  const [loading, setLoading] = useState(true);
  const [service, setService] = useState(null);
  const [profile, setProfile] = useState(null);
  const [ownerUser, setOwnerUser] = useState(null);
  const [quote, setQuote] = useState(null);

  // Booking State
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('Español');
  const [selectedModality, setSelectedModality] = useState('Remoto');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const stripeCardRef = useRef(null); // acceso imperativo al CardElement de Stripe
  const [bookingNote, setBookingNote] = useState('');
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [newAddress, setNewAddress] = useState({
    nombre: '', calle: '', numeroExt: '', numeroInt: '',
    colonia: '', municipio: '', estado: '', cp: '',
    pais: '', referencia: '', direccion: '', place_id: ''
  });
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [newInvoice, setNewInvoice] = useState({
    nombre: '', rfc: '', razonSocial: '', direccionFiscal: '', cp: '', regimenFiscal: '', usoCFDI: ''
  });
  const [requireInvoice, setRequireInvoice] = useState(false);

  // Código promocional
  const [promoCodigo, setPromoCodigo] = useState('');
  const [promoAplicado, setPromoAplicado] = useState(null); // { codigo, descuentoPct }
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  // Confirmation state
  const [confirmed, setConfirmed] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [validationError, setValidationError] = useState('');
  // Campo obligatorio faltante para pintarlo de rojo: horario|numero|nombre|expiracion|cvv
  const [campoError, setCampoError] = useState('');
  // Marca el mensaje del alert y el campo que hay que resaltar.
  const marcarError = (msg, campo = '') => { setValidationError(msg); setCampoError(campo); };

  const dispatch = useDispatch();
  const authUser = useSelector(state => state.user.user);
  const userProfileClient = useSelector(state => state.profile.profile);

  useEffect(() => {
    const fetchServiceAndQuote = async () => {
      try {
        const data = await getPublicServiceDetail(userId, serviceId);
        if (data && data.service) {
          setService(data.service);
          setProfile(data.profile);
          setOwnerUser(data.ownerUser);

          if (data.service.idiomas && data.service.idiomas.length > 0) {
            setSelectedLanguage(data.service.idiomas[0]);
          }
          if (data.service.modalidades && data.service.modalidades.length > 0) {
            setSelectedModality(data.service.modalidades[0]);
          }
        }

        if (quoteId) {
          const quoteData = await dispatch(getQuoteDetail(quoteId));
          if (quoteData) {
            setQuote(quoteData);
            setSelectedLanguage(quoteData.idioma || 'Español');
            const ofrecidas = quoteData.modalidades?.length
              ? quoteData.modalidades
              : [quoteData.modalidad].filter(Boolean);
            setSelectedModality(ofrecidas[0] || 'Remoto');
          }
        }
      } catch (err) {
        console.error("Error fetching service or quote for booking:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchServiceAndQuote();
    dispatch(getCurrentProfile());
  }, [userId, serviceId, quoteId, dispatch]);

  useEffect(() => {
    if (authUser && String(authUser._id) === String(userId)) {
      navigate('/home');
    }
  }, [authUser, userId, navigate]);

  // Al seleccionar horario se quita su marca de error (si la tenía).
  const handleTimeSelect = (t) => {
    setSelectedTime(t);
    if (campoError === 'horario') setCampoError('');
  };

  const handleAplicarPromo = async () => {
    const cod = promoCodigo.trim();
    if (!cod) return;
    setPromoLoading(true);
    setPromoError('');
    const catId = service?.categoria?._id || service?.categoria || '';
    const res = await validarPromo(cod, catId);
    if (res?.valido) {
      setPromoAplicado({
        codigo: res.codigo,
        tipo: res.tipo,
        descuentoPct: res.descuentoPct,
        montoFijo: res.montoFijo,
        montoMinimo: res.montoMinimo
      });
      setPromoError('');
    } else {
      setPromoAplicado(null);
      setPromoError(res?.msg || 'Código no válido o expirado.');
    }
    setPromoLoading(false);
  };

  const handleQuitarPromo = () => {
    setPromoAplicado(null);
    setPromoCodigo('');
    setPromoError('');
  };

  // Solo se pide método de pago si hay un costo numérico y los pagos están configurados.
  // Con cotización manda el precio NEGOCIADO (es lo que el backend cobrará).
  // El cargo NO ocurre al reservar: se procesa cuando el proveedor acepta.
  const costoBase = quote ? parseFloat(quote.costo) : parseFloat(service?.costoServicio);
  const requierePago = pagosDisponibles() && costoBase > 0;

  // Validaciones previas al pago (comunes a tarjeta, SPEI/OXXO y wallet). Devuelve false
  // y marca el campo si falta algo. La wallet la usa antes de abrir la hoja nativa.
  const validarReserva = () => {
    if (!selectedTime) {
      marcarError('Por favor selecciona un horario disponible.', 'horario');
      return false;
    }
    return true;
  };

  // Arma los datos fiscales (si aplica) y crea la reserva. `pago` ya trae el método
  // resuelto: { paymentMethodId } para tarjeta/wallet, o { metodoPago } para SPEI/OXXO.
  const crearReservaConPago = async (pago) => {
    // Factura: si la solicita, se envían los datos fiscales (los del formulario nuevo
    // o los del perfil fiscal guardado que haya elegido) para que el superadmin emita el CFDI
    let datosFactura = null;
    if (requireInvoice) {
      if (selectedInvoiceId && selectedInvoiceId !== 'new') {
        const saved = (userProfileClient?.facturacion || []).find(f => f._id === selectedInvoiceId);
        datosFactura = saved
          ? {
              nombre: saved.nombre || '', rfc: saved.rfc || '',
              razonSocial: saved.razonSocial || saved.nombre || '',
              direccionFiscal: saved.direccionFiscal || '', cp: saved.cp || '',
              regimenFiscal: saved.regimenFiscal || '', usoCFDI: saved.usoCFDI || ''
            }
          : newInvoice;
      } else {
        datosFactura = newInvoice;
      }
    }

    const duracionMinutos = getDurationMinutes(service.duracionEstimada, service.unidadDuracion);
    const result = await dispatch(createBooking({
      servicio: service._id,
      proveedor: ownerUser?._id,
      quoteId: quote?._id,
      fecha: selectedDate.toISOString().split('T')[0],
      hora: selectedTime,
      duracionMinutos: duracionMinutos || 60,
      modalidad: selectedModality,
      nota: bookingNote,
      codigoPromo: promoAplicado?.codigo || '',
      requiereFactura: requireInvoice,
      datosFactura,
      ...pago
    }));

    if (result?.success) {
      setBookingResult(result.booking);
      setConfirmed(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return true;
    }
    setValidationError(result?.msg || 'Error al confirmar la reserva. Intenta de nuevo.');
    return false;
  };

  const handleConfirm = async () => {
    if (!validarReserva()) return;

    setIsConfirming(true);
    try {
      // Stripe: guarda/autentica la tarjeta con un SetupIntent y devuelve el paymentMethodId.
      let pago = {};
      if (requierePago) {
        if (paymentMethod === 'card') {
          try {
            const paymentMethodId = await stripeCardRef.current.confirmar();
            pago = { paymentMethodId };
          } catch (tokenErr) {
            setValidationError(tokenErr.message || 'No se pudo validar la tarjeta.');
            setIsConfirming(false);
            return;
          }
        } else {
          pago = { metodoPago: paymentMethod };
        }
      }

      await crearReservaConPago(pago);
    } catch (err) {
      setValidationError('Error al confirmar la reserva. Intenta de nuevo.');
    } finally {
      setIsConfirming(false);
    }
  };

  // Apple/Google Pay: la hoja nativa ya autorizó y guardó la tarjeta (pm_...). El botón
  // de la wallet es su propio CTA, así que aquí solo creamos la reserva con ese método.
  const handleWalletPago = async (paymentMethodId) => {
    setIsConfirming(true);
    try {
      await crearReservaConPago({ paymentMethodId });
    } catch (err) {
      setValidationError('Error al confirmar la reserva. Intenta de nuevo.');
    } finally {
      setIsConfirming(false);
    }
  };

  if (loading || !service) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (confirmed) {
    return (
      <BookingConfirmation
        booking={bookingResult}
        service={service}
        ownerUser={ownerUser}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        selectedLanguage={selectedLanguage}
        selectedModality={selectedModality}
        paymentMethod={paymentMethod}
      />
    );
  }

  return (
    <Grid maxWidth="xl" sx={{ mt: 12, mb: 8 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            mr: 2,
            bgcolor: '#F1F5F9',
            color: '#0E2465',
            '&:hover': { bgcolor: '#E2E8F0' }
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: 16, ml: 0.5 }} />
        </IconButton>
        <Typography variant="subtitle1" fontWeight="bold" color="#0E2465" sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)}>
          Atrás
        </Typography>
      </Box>

      <Grid container spacing={1}>
        <Grid item size={{ xs: 12, md: 7, lg: 8 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <BookingDateTime
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
              selectedTime={selectedTime}
              onTimeSelect={handleTimeSelect}
              service={service}
              error={campoError === 'horario'}
            />

            <BookingOptions
              languages={quote ? [quote.idioma] : (service?.idiomas || ['Español'])}
              modalities={quote
                ? (quote.modalidades?.length ? quote.modalidades : [quote.modalidad].filter(Boolean))
                : (service?.modalidades || ['Remoto'])}
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
              selectedModality={selectedModality}
              setSelectedModality={setSelectedModality}
              disabled={!!quote}
              modalityDisabled={!!quote && (quote.modalidades?.length || (quote.modalidad ? 1 : 0)) <= 1}
            />

            {selectedModality === 'Domicilio' && (
              <BookingAddress
                addresses={userProfileClient?.direcciones || []}
                selectedAddressId={selectedAddressId}
                setSelectedAddressId={setSelectedAddressId}
                newAddress={newAddress}
                setNewAddress={setNewAddress}
              />
            )}

            <BookingMessage
              value={bookingNote}
              onChange={setBookingNote}
            />

            {requierePago && (
              <BookingPayment
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                stripeCardRef={stripeCardRef}
                onWalletPago={handleWalletPago}
                validarPago={validarReserva}
                onWalletError={setValidationError}
              />
            )}

            <Box sx={{
              p: 4,
              borderRadius: '24px',
              bgcolor: 'white',
              border: '1px solid #F1F5F9',
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: requireInvoice ? 4 : 0
              }}>
                <Typography variant="h6" fontWeight="900" color="#0E2465">
                  ¿Requieres factura?
                </Typography>
                <Box
                  component="span"
                  onClick={() => setRequireInvoice(!requireInvoice)}
                  sx={{
                    width: 54,
                    height: 28,
                    borderRadius: '20px',
                    bgcolor: requireInvoice ? '#0E2465' : '#E2E8F0',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: '0.3s'
                  }}
                >
                  <Box sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: 'white',
                    position: 'absolute',
                    top: 3,
                    left: requireInvoice ? 29 : 3,
                    transition: '0.3s',
                    boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
                  }} />
                </Box>
              </Box>

              {requireInvoice && (
                <BookingInvoice
                  facturacion={userProfileClient?.facturacion || []}
                  selectedInvoiceId={selectedInvoiceId}
                  setSelectedInvoiceId={setSelectedInvoiceId}
                  newInvoice={newInvoice}
                  setNewInvoice={setNewInvoice}
                />
              )}
            </Box>
          </Box>
        </Grid>

        <Grid item size={{ xs: 12, md: 5, lg: 4 }}>
          <BookingSummary
            service={service}
            profile={profile}
            ownerUser={ownerUser}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            selectedLanguage={selectedLanguage}
            selectedModality={selectedModality}
            paymentMethod={paymentMethod}
            quote={quote}
            onConfirm={handleConfirm}
            isConfirming={isConfirming}
            promoCodigo={promoCodigo}
            setPromoCodigo={setPromoCodigo}
            promoAplicado={promoAplicado}
            promoError={promoError}
            promoLoading={promoLoading}
            onAplicarPromo={handleAplicarPromo}
            onQuitarPromo={handleQuitarPromo}
          />
        </Grid>
      </Grid>

      <Snackbar
        open={!!validationError}
        autoHideDuration={4000}
        onClose={() => setValidationError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setValidationError('')}
          sx={{
            borderRadius: '12px', fontWeight: 700, fontSize: '1rem', py: 1.75, px: 3,
            alignItems: 'center', boxShadow: '0 10px 28px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': { fontSize: 28 }
          }}>
          {validationError}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default BookingContainer;
